import { useState } from "react";
import { useMutation } from "react-query";
import { t } from "@/i18n-js/instance";
import { useCurrentCommunityContext } from "@circle-react/apps/AuthenticationApp";
import { authenticationAppRoutes } from "@circle-react/components/OnboardingPage/helpers/routes";
import { reactQueryPost } from "@circle-react/helpers/backendRequestHelpers";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { Modal } from "@circle-react-uikit/ModalV2";
import { useToast } from "@circle-react-uikit/ToastV2";
import { OTPInput } from "../shared/Otp/OtpInput";
import { AuthenticationTitle } from "./components/AuthenticationTitle";
import { OtpFooter } from "./components/OtpFooter";
import { OtpHeader } from "./components/OtpHeader";
import { OtpSubheader } from "./components/OtpSubheader";
import { useContainerWidth } from "./useContainerWidth";

interface OtpChallengeModalProps {
  email?: string;
  error?: string | null;
  isLoading?: boolean;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (otpCode: string) => void | Promise<void>;
  otpType: "authentication" | "verification";
}

interface ResendOtpResult {
  message: string;
}

interface ResendOtpRequestBody {
  user: {
    email: string;
  };
}

const ALLOWED_RESEND_COUNT = 1;

export const OtpChallengeModal = ({
  email,
  otpType,
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
  error,
}: OtpChallengeModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useToast();

  const { width, containerRef } = useContainerWidth();

  const resendOtpMutation = useMutation(
    (body: ResendOtpRequestBody) =>
      reactQueryPost(authenticationAppRoutes.resendOtp(), body),
    {
      onSuccess: (data: ResendOtpResult) => {
        toast.success(data?.message);
      },
      onError: (error: unknown) => {
        console.error(error);
        const err = error as { body?: { error?: string }; message?: string };
        toast.error(
          err.body?.error ||
            err.message ||
            t("otp_challenge_modal.resend_failed"),
        );
      },
    },
  );

  const handleSubmit = async (otp: string) => {
    setIsSubmitting(true);
    try {
      await onSubmit(otp);
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = () => {
    if (!email) {
      return;
    }

    resendOtpMutation.mutate({ user: { email } });
  };

  const isResending = resendOtpMutation.isLoading;
  const isDisabled = isSubmitting || isLoading || isResending;

  const { currentCommunity } = useCurrentCommunityContext();

  const isAuthenticating = otpType === "authentication";
  const isVerifying = otpType === "verification";

  return (
    <Modal isOpen={isOpen} className="!z-[2000]">
      <Modal.Overlay />
      <Modal.Content
        size={isAuthenticating ? "screen" : "auto"}
        className={isVerifying ? "!w-auto !rounded-xl" : undefined}
      >
        <Modal.Body className={isVerifying ? "!p-0" : undefined}>
          <div
            className={classNames(
              "flex h-full flex-col items-center justify-center",
              {
                "mx-8": isAuthenticating,
              },
            )}
            ref={containerRef}
          >
            {isAuthenticating && (
              <AuthenticationTitle
                currentCommunity={currentCommunity}
                otpType={otpType}
              />
            )}
            <div className="flex justify-center">
              <div
                className={classNames(
                  "w-full rounded-xl p-6 sm:min-w-[468px] sm:max-w-lg",
                  isAuthenticating ? "border border-[#E4E7EB]" : "border-0",
                )}
              >
                <OtpHeader
                  email={email}
                  onClose={onClose}
                  isDisabled={isDisabled}
                />

                <div className="space-y-6">
                  <OtpSubheader email={email} isVerifying={isVerifying} />

                  <div className="flex justify-center gap-6">
                    <OTPInput
                      onComplete={handleSubmit}
                      disabled={isDisabled}
                      error={!!error}
                      containerWidth={width}
                    />
                  </div>

                  <OtpFooter
                    handleResend={handleResend}
                    isDisabled={isDisabled}
                    allowedResendCount={ALLOWED_RESEND_COUNT}
                  />
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal.Content>
    </Modal>
  );
};

OtpChallengeModal.displayName = "OtpChallengeModal";
